import React from "react";
import { useParams, useHistory } from "react-router-dom";
import { useViewState } from "utils/useViewState";
import { Select, MenuItem, FormControl, InputLabel, Button } from "@material-ui/core";
import { css } from "@emotion/css";
import { useQuestionnaireByName, usePublishedQuestionnaireHistory } from "./queries";
import type { PublishedQuestionnaireHistoryItem } from "./api";
import { parseRef, refKey } from "./useLocaleDiffPreviews";
import type { SnapshotRef } from "./useLocaleDiffPreviews";
import DiffShell from "./components/DiffShell";
import RestoreDialog from "./components/RestoreDialog";
import { deployedGroups, isDeployedTo } from "./rolloutText";

const refLabel = (r: SnapshotRef, history: PublishedQuestionnaireHistoryItem[], draftExists: boolean): string => {
  if (r.kind === "draft") return draftExists ? "Draft" : "Draft (missing)";
  const item = history.find(h => h.id === r.id);
  if (!item) return `Published #${r.id}`;
  const ts = new Date(item.published_at).toLocaleString();
  const groups = deployedGroups(item.rollout);
  return `${ts}${item.is_active ? " (current)" : ""}${groups.length > 0 ? ` · ${groups.join(", ")}` : ""}`;
};

const Compare: React.VFC = () => {
  const { name: rawName } = useParams<{ name: string }>();
  const name = decodeURIComponent(rawName);
  const history = useHistory();
  const [view, setView] = useViewState({ before: "", after: "" });

  const draftQuery = useQuestionnaireByName(name);
  const historyQuery = usePublishedQuestionnaireHistory(name);

  const draft = draftQuery.data ?? null;
  const publishedHistory = historyQuery.data ?? [];
  const activePublished = publishedHistory.find(p => p.is_active) ?? null;
  const latestPublished = publishedHistory[0] ?? null;
  const prodPublished = publishedHistory.find(p => isDeployedTo(p.rollout, "prod")) ?? null;

  const defaultAfter: SnapshotRef | null = draft
    ? { kind: "draft" }
    : latestPublished
      ? { kind: "published", id: latestPublished.id }
      : null;

  const after = parseRef(view.after) ?? defaultAfter;

  const defaultBeforeItem =
    after?.kind === "published"
      ? prodPublished && prodPublished.id !== after.id
        ? prodPublished
        : publishedHistory[publishedHistory.findIndex(p => p.id === after.id) + 1]
      : (prodPublished ?? activePublished ?? latestPublished);

  const before: SnapshotRef | null =
    parseRef(view.before) ?? (defaultBeforeItem ? { kind: "published", id: defaultBeforeItem.id } : null);
  const [restoreOpen, setRestoreOpen] = React.useState(false);

  // restore always targets the draft; the source is the published side of the comparison
  const restoreSource =
    draft && before?.kind === "published" && after?.kind === "draft"
      ? before
      : draft && after?.kind === "published" && before?.kind === "draft"
        ? after
        : null;

  const updateQuery = (next: { before?: SnapshotRef | null; after?: SnapshotRef | null }) =>
    setView(Object.fromEntries(Object.entries(next).map(([key, ref]) => [key, ref ? refKey(ref) : ""])));

  const snapshotOptions: SnapshotRef[] = [
    ...(draft ? [{ kind: "draft" as const }] : []),
    ...publishedHistory.map(p => ({ kind: "published" as const, id: p.id })),
  ];

  const renderSnapshotSelect = (
    label: string,
    value: SnapshotRef | null,
    onChange: (next: SnapshotRef | null) => void
  ) => (
    <FormControl size="small" variant="outlined" className={styles.snapshotSelect}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value ? refKey(value) : ""}
        label={label}
        onChange={e => onChange(parseRef(String(e.target.value)))}
      >
        {snapshotOptions.map(opt => (
          <MenuItem key={refKey(opt)} value={refKey(opt)}>
            {refLabel(opt, publishedHistory, !!draft)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const actions = (
    <>
      {renderSnapshotSelect("Before", before, next => updateQuery({ before: next }))}
      {renderSnapshotSelect("After", after, next => updateQuery({ after: next }))}
      <Button variant="outlined" disabled={!restoreSource} onClick={() => setRestoreOpen(true)}>
        Restore draft...
      </Button>
      <Button variant="outlined" onClick={() => history.push("/health-forms/published")}>
        Cancel
      </Button>
    </>
  );

  const isLoading = draftQuery.isLoading || historyQuery.isLoading;
  const error = draftQuery.error || historyQuery.error ? "Failed to load versions for this questionnaire" : null;

  return (
    <>
      {draft && restoreSource && (
        <RestoreDialog
          open={restoreOpen}
          onClose={() => setRestoreOpen(false)}
          draftId={draft.id}
          publishedId={restoreSource.id}
          snapshotLabel={refLabel(restoreSource, publishedHistory, !!draft)}
        />
      )}
      <DiffShell
        title={`Compare: ${name}`}
        before={before}
        after={after}
        draftId={draft?.id ?? null}
        previousLabel={before ? refLabel(before, publishedHistory, !!draft) : "Before"}
        nextLabel={after ? refLabel(after, publishedHistory, !!draft) : "After"}
        emptyMessage="Nothing to compare — this name has no draft or published versions yet."
        actions={actions}
        isLoading={isLoading}
        error={error}
      />
    </>
  );
};

export default Compare;

const styles = {
  snapshotSelect: css`
    min-width: 220px;
  `,
};
